/**
 * Krok UXR3 — PROUZEK FAZI O PETI KROCICH. JEDNA FUNKCE, TRI MISTA.
 *
 * Zadani UXR3 bod 5: „prouzek fazi o 5 krocich (Založení · Prověření · Report
 * pro klienta · Inzerce · Smlouva a předání) — mapovani z dnesnich fazi a bran
 * je ROZHODNUTI v reportu, JEDNA FUNKCE, kterou vola desktop i mobil i Dnešek."
 *
 * Tohle je mapovani fazi na kroky. Od kroku UXV4a nad nim stoji JEDNA
 * funkce `lib/case-progress.ts` (`caseProgressView`), ktera k nemu pridava
 * procenta a vetu „co chybí" — tu vola hlavicka detailu zakazky, Dnesek
 * i kazde dalsi misto s prouzkem postupu. Rovnou `caseProgress` uz nikdo
 * nevola, aby se procenta nepocitala na dvou mistech dvakrat.
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTI UXR3-FAZE — mapovani deseti fazi stavoveho automatu na pet kroku
 *
 * Stavovy automat zakazky (`KANBAN_STAGE_ORDER`,
 * `packages/case/src/internal/state-machine.ts`) ma deset fazi. Predloha ma
 * pet kroku, protoze pet je to, co makleri rika neco o POSTUPU („uz jsme za
 * proverenim"); deset je pohled na stavovy automat. Faze se proto NEPREJMENOVAVAJI
 * (to by znamenalo lhat o stavu zakazky — tentyz duvod jako ROZHODNUTI UXR1-3
 * a UXR2-2) a nic se z nich nemaze: pet kroku je POHLED, ktery deset fazi
 * seskupuje. Puvodni faze zustava v `CasePhase.stages` i na obrazovce
 * (hlavicka ji pise v podtitulku, kanban ve sloupci).
 *
 * | krok predlohy        | faze stavoveho automatu                             |
 * | -------------------- | --------------------------------------------------- |
 * | Založení             | `intake`                                            |
 * | Prověření            | `audit`                                             |
 * | Report pro klienta   | `preparation`                                       |
 * | Inzerce              | `listing`, `viewings`                               |
 * | Smlouva a předání    | `negotiation`, `reservation`, `contracting`, `handover` |
 *
 * `closed` = vsech pet kroku hotovo (zakazka dosla na konec),
 * `cancelled` = zadny krok neni „ted" (zakazka se uz nikam neposouva), hotove
 * kroky zustavaji hotove podle toho, kam zakazka doopravdy dosla.
 *
 * Proc `preparation` = „Report pro klienta": priprava zakazky je v REOSu
 * presne ta faze, kdy je proverení za nami a vyrabi se podklad pro klienta
 * (report, viz `ClientReportCard`) — inzerovat se zacina az fazi `listing`.
 * ---------------------------------------------------------------------------
 */

import { t } from '@reos/i18n';
import { CANCELLED_STAGE } from './case-stages.js';
import { CASE_AREAS_PREPARING } from './texts.js';

const cs = { locale: 'cs' } as const;

/**
 * Krok PIL3 bod 8 (nalez F07): ctvrty stav `unavailable` — krok, jehoz oblast
 * REOS jeste nema (detail ji kresli jako „Připravuje se"). Neni `done`
 * (odskrtnuti by tvrdilo, ze se oblast odbavila) ani `later` (zakazka uz je
 * za nim). Prouzek ho kresli bez znacky.
 */
export type CasePhaseState = 'done' | 'now' | 'later' | 'unavailable';

/** Id kroku — stabilni klic pro `data-testid` a pro katalog textu. */
export const CASE_PHASE_IDS = ['setup', 'review', 'report', 'listing', 'closing'] as const;
export type CasePhaseId = (typeof CASE_PHASE_IDS)[number];

/** Mapovani z tabulky v hlavicce souboru — JEDINY zdroj pravdy. */
const PHASE_STAGES: Readonly<Record<CasePhaseId, readonly string[]>> = {
  setup: ['intake'],
  review: ['audit'],
  report: ['preparation'],
  listing: ['listing', 'viewings'],
  closing: ['negotiation', 'reservation', 'contracting', 'handover'],
};

/**
 * Faze, ktera v mapovani zamerne NENI, protoze neni krokem cesty:
 * `closed` je stav ZA poslednim krokem, `cancelled` je odbocka mimo ni.
 * Test „kazda faze stavoveho automatu ma svuj krok" tenhle vycet odecita,
 * takze pridani nove faze do `KANBAN_STAGE_ORDER` bez mapovani spadne.
 */
export const PHASELESS_STAGES: readonly string[] = ['closed', CANCELLED_STAGE];

/**
 * Krok PIL3 bod 8 (nalez F07) — ktera SEKCE detailu kazdy krok zastupuje.
 *
 * Potreba jen tam, kde se krok muze trefit do oblasti, kterou REOS jeste nema
 * (`CASE_AREAS_PREPARING`). Kroky bez sekce (`null`) se chovaji jako driv.
 * Mapovani stoji TADY, vedle `PHASE_STAGES`, aby „co krok znamena" bylo na
 * jednom miste.
 */
export const PHASE_SECTION: Readonly<Record<CasePhaseId, string | null>> = {
  setup: null,
  review: null,
  report: null,
  listing: 'inzerce',
  closing: null,
};

/**
 * Krok QAFIX11 bod 1 (nalez V5 QA 21. a 22. 10.) — TENTYZ MAPOVANI NESE
 * I KLIKATELNOST KROKU V PROUZKU.
 *
 * UXD4 vzal kroku „ted" barvu akcentu, aby `<span>` nepusobil jako odkaz;
 * QA 22. 10. ale ukazala, ze makléř na „Inzerce" klika dal — krok NAZEV
 * oblasti nese, takze se chova jako navigace. Misto dalsiho ubirani vzhledu
 * dostava krok, ktery SEKCI detailu ma, skutecnou akci. Zdroj je ten samy
 * zapis `PHASE_SECTION`, ne druha tabulka vedle nej.
 */
export function casePhaseSection(id: CasePhaseId): string | null {
  return PHASE_SECTION[id];
}

function phaseAreaPreparing(id: CasePhaseId): boolean {
  const section = PHASE_SECTION[id];
  return section !== null && (CASE_AREAS_PREPARING as readonly string[]).includes(section);
}

export interface CasePhase {
  readonly id: CasePhaseId;
  readonly label: string;
  readonly state: CasePhaseState;
  /** Faze stavoveho automatu, ktere tenhle krok zastupuje. */
  readonly stages: readonly string[];
  /**
   * Podil rozpracovanosti kroku „ted" (0–1), kdyz ho umime zmerit.
   * Dnes jen u „Prověření" z poctu overenych oblasti; jinak `null` a prouzek
   * se kresli cely — nedopocitava se nic, co nevime.
   */
  readonly progress: number | null;
}

export interface CaseProgress {
  readonly phases: readonly CasePhase[];
  /** Poradi kroku „ted" od 1; `null` u zrusene zakazky a u nezname faze. */
  readonly currentIndex: number | null;
  readonly current: CasePhase | null;
  readonly total: number;
  /** Zrusena zakazka — prouzek uz se nikam neposouva. */
  readonly cancelled: boolean;
}

export interface CaseProgressInput {
  /**
   * Rozpracovanost kroku „Prověření" — kolik oblasti je overeno z kolika.
   * Pochazi z `gateProgress` (`lib/case-gates.ts`), tedy z TEHOZ vypoctu,
   * jaky kresli seznam oblasti; dve ruzna cisla na jedne obrazovce by si
   * odporovala (nalez AUD1 28. 9., viz hlavicka `case-gates.ts`).
   */
  readonly review?: { readonly done: number; readonly total: number } | undefined;
}

function phaseLabel(id: CasePhaseId): string {
  return t(`webAdmin.casePhases.${id}` as never, cs);
}

/** Index kroku, do ktereho faze patri; `-1` kdyz fazi neznáme. */
function phaseIndexOfStage(stage: string): number {
  return CASE_PHASE_IDS.findIndex((id) => PHASE_STAGES[id].includes(stage));
}

/**
 * Pet kroku pro zadanou fazi zakazky.
 *
 * `stage` je `cases.stage` (hodnota stavoveho automatu). Neznamou hodnotu
 * funkce NEHADA: vsech pet kroku je `later` a `currentIndex` je `null`, takze
 * obrazovka nic netvrdi.
 */
export function caseProgress(stage: string | null, input: CaseProgressInput = {}): CaseProgress {
  const cancelled = stage === CANCELLED_STAGE;
  // `closed` v zadnem kroku neni — je to stav ZA poslednim krokem.
  const finished = stage === 'closed';
  const index = stage === null || cancelled || finished ? -1 : phaseIndexOfStage(stage);
  // Faze, ktera v mapovani chybi, by tise znamenala „nic hotovo". Hlida test;
  // tady je to poctiva neznama hodnota, ne nula.
  const known = index >= 0 || finished;

  const phases = CASE_PHASE_IDS.map((id, position): CasePhase => {
    const reached: CasePhaseState = finished
      ? 'done'
      : !known
        ? 'later'
        : position < index
          ? 'done'
          : position === index
            ? 'now'
            : 'later';
    /*
     * Krok PIL3 bod 8 (F07): oblast, kterou REOS jeste nema, se NEODSKRTAVA.
     * Zakazka fazi opravdu prosla (proto `reached === 'done'`), ale v REOSu
     * se v te oblasti nic odbavit nedalo — zalozka je placeholder. Rika to
     * tedy `unavailable`, ne `done`.
     */
    const state: CasePhaseState =
      reached === 'done' && phaseAreaPreparing(id) ? 'unavailable' : reached;
    const review = input.review;
    const progress =
      state === 'now' && id === 'review' && review !== undefined && review.total > 0
        ? Math.min(1, Math.max(0, review.done / review.total))
        : null;
    return { id, label: phaseLabel(id), state, stages: PHASE_STAGES[id], progress };
  });

  const current = phases.find((phase) => phase.state === 'now') ?? null;
  return {
    phases,
    currentIndex: current === null ? null : CASE_PHASE_IDS.indexOf(current.id) + 1,
    current,
    total: CASE_PHASE_IDS.length,
    cancelled: cancelled,
  };
}
